import * as XLSX from 'xlsx';
import { Movement } from '../types/movement';
import { Pilgrim } from '../types/pilgrim';
import { Package } from '../types/package';
import { Flight } from '../types/flight';
import { getMovementPilgrims } from '../services/movements/pilgrimMatching';

type ExportRow = {
  movement_type: string;
  movement_date: string;
  movement_time: string;
  from_location: string;
  from_city: string;
  to_location: string;
  to_city: string;
  pilgrim_name: string;
  pilgrim_passport: string;
  pilgrim_gender: string;
  pilgrim_country: string;
  pilgrim_package: string;
  pilgrim_mobile: string;
  estimated_arrival: string;
};

export const exportFilteredMovements = (
  movements: Movement[],
  pilgrims: Record<string, Pilgrim>,
  packages: Record<string, Package>,
  flights: Record<string, Flight>,
  translations?: any
) => {
  try {
    const rows: ExportRow[] = [];

    // Process each movement and its pilgrims
    movements.forEach(movement => {
      const movementPilgrims = getMovementPilgrims(movement, pilgrims, packages, flights);

      movementPilgrims.forEach(pilgrim => {
        rows.push({
          movement_type: getMovementTypeLabel(movement.type, translations),
          movement_date: movement.departureDate,
          movement_time: movement.departureTime,
          from_location: movement.from.name,
          from_city: getCityLabel(movement.from.city, translations),
          to_location: movement.to.name,
          to_city: getCityLabel(movement.to.city, translations),
          pilgrim_name: pilgrim.Name,
          pilgrim_passport: pilgrim.Passport_Number,
          pilgrim_gender: pilgrim.Gender === 'male' 
            ? translations?.common?.male || 'Male' 
            : translations?.common?.female || 'Female',
          pilgrim_country: pilgrim.Residence_Country,
          pilgrim_package: pilgrim.package_nickName,
          pilgrim_mobile: pilgrim.Mobile_Number,
          estimated_arrival: movement.arrivalTime
        });
      });
    });

    // Create worksheet with localized headers
    const ws = XLSX.utils.json_to_sheet(rows);
    
    XLSX.utils.sheet_add_aoa(ws, [[
      translations?.movementExport?.headers?.type || 'Movement Type',
      translations?.movementExport?.headers?.date || 'Date',
      translations?.movementExport?.headers?.time || 'Time',
      translations?.movementExport?.headers?.from || 'From',
      translations?.movementExport?.headers?.city || 'City',
      translations?.movementExport?.headers?.to || 'To',
      translations?.movementExport?.headers?.city || 'City',
      translations?.movementExport?.headers?.name || 'Pilgrim Name',
      translations?.movementExport?.headers?.passport || 'Passport Number',
      translations?.movementExport?.headers?.gender || 'Gender',
      translations?.movementExport?.headers?.country || 'Country',
      translations?.movementExport?.headers?.package || 'Package',
      translations?.movementExport?.headers?.mobile || 'Mobile Number',
      translations?.movementExport?.headers?.arrival || 'Estimated Arrival'
    ]], { origin: 'A1' });

    // Set column widths
    ws['!cols'] = [
      { wch: 20 }, { wch: 15 }, { wch: 15 }, 
      { wch: 25 }, { wch: 15 }, { wch: 25 }, 
      { wch: 15 }, { wch: 30 }, { wch: 15 }, 
      { wch: 10 }, { wch: 20 }, { wch: 25 }, 
      { wch: 15 }, { wch: 15 }
    ];

    // Create workbook with localized sheet name
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb, 
      ws, 
      translations?.movementExport?.sheetName || 'Movement Details'
    );

    // Generate filename
    const date = new Date().toISOString().split('T')[0];
    const filename = `movements-${date}.xlsx`;

    XLSX.writeFile(wb, filename);
  } catch (error) {
    console.error('Error exporting movements:', error);
    throw new Error(translations?.movementExport?.exportError || 'Failed to export movements');
  }
};

const getMovementTypeLabel = (type: string, translations?: any): string => {
  switch (type) {
    case 'arrival': 
      return translations?.movementTypes?.arrival || 'Pilgrims Arrival';
    case 'departure': 
      return translations?.movementTypes?.departure || 'Pilgrims Departure';
    case 'hotel_transfer': 
      return translations?.movementTypes?.hotelTransfer || 'Hotel Transfer';
    case 'camp_departure': 
      return translations?.movementTypes?.campDeparture || 'Camp Departure';
    case 'camp_return': 
      return translations?.movementTypes?.campReturn || 'Camp Return';
    case 'hotel_transfer_madinah': 
      return translations?.movementTypes?.hotelTransferMadinah || 'Transfer to Madinah';
    default: 
      return type;
  }
};

const getCityLabel = (city: string, translations?: any): string => {
  switch (city) {
    case 'makkah': return translations?.cities?.makkah || 'Makkah';
    case 'madinah': return translations?.cities?.madinah || 'Madinah';
    case 'jeddah': return translations?.cities?.jeddah || 'Jeddah';
    default: return city;
  }
};